import config from "../../config.json";
import { getEnvironmentVariables } from "../../common/utils";
import { apiCall } from "../../common/http/apiCall";
import { General } from "../../common/constants";
import { Dashboard } from "../../common/constants/common";

// Whether the organisation's plans allow the action, as Marketplace's action-eligibility check
// answers it. Returns { success: true, data } with Marketplace's answer ({ allowed, data }, the
// data naming the products that offer the action when it is not allowed; a plan whose limit is
// used up answers its QuotaExceeded code in place of allowed) or apiCall's normalised failure:
// 502 (SubscriptionApiCallFailed) when the organisation's plans could not be read, 500 for
// anything else that failed on Marketplace's side, 400 for an empty organisation id or action,
// and no status at all when nothing answered.
export const isActionEligible = async ({
  tenantId,
  organisationId,
  action,
  accessToken,
  refreshToken,
}) => {
  let baseUrl = getEnvironmentVariables().REACT_APP_MARKETPLACE_API_URL;

  let url = config.marketPlace.organisations.isActionEligibleUrl
    .replace("{tenantId}", tenantId)
    .replace("{organisationId}", organisationId)
    .replace("{actionName}", action);

  let data = {};

  var result = await apiCall({
    data,
    baseUrl,
    accessToken: accessToken,
    refreshToken: refreshToken,
    url,
    method: "GET",
  });

  if (result.success) {
    return { success: true, data: result.response.data };
  }

  return result;
};

export const fetchUserAppSpaces = async ({
  tenantId,
  organisationId,
  accessToken,
  refreshToken,
}) => {
  let baseUrl = getEnvironmentVariables().REACT_APP_MARKETPLACE_API_URL;

  let url = config.marketPlace.engineeringOps.applications.fetchMyAppSpaces
    .replace("{tenantId}", tenantId)
    .replace("{organisationId}", organisationId);

  let data = {};

  var result = await apiCall({
    data,
    baseUrl,
    accessToken: accessToken,
    refreshToken: refreshToken,
    url,
    method: "GET",
  });

  let response = result.response;
  if (response?.status === 200) {
    return response.data;
  }
  return {};
};

export const fetchUserAppSpace = async ({
  tenantId,
  appSpaceId,
  accessToken,
  refreshToken,
}) => {
  let baseUrl = getEnvironmentVariables().REACT_APP_MARKETPLACE_API_URL;

  let url = config.marketPlace.engineeringOps.applications.fetchMyAppSpaceUrl
    .replace("{appSpaceId}", appSpaceId)
    .replace("{tenantId}", tenantId);

  let data = {};

  var result = await apiCall({
    data,
    baseUrl,
    accessToken: accessToken,
    refreshToken: refreshToken,
    url,
    method: "GET",
  });

  let response = result.response;
  if (response?.status === 200) {
    return response.data;
  }
  return {};
};

export const fetchOrganisationTeams = async ({
  tenantId,
  orgId,
  accessToken,
  refreshToken,
}) => {
  let baseUrl = getEnvironmentVariables().REACT_APP_MARKETPLACE_API_URL;
  let url = config.marketPlace.organisations.fetchOrganisationTeamsUrl
    .replace("{tenantId}", tenantId)
    .replace("{orgId}", orgId);

  let data = {};

  var result = await apiCall({
    data,
    baseUrl,
    accessToken: accessToken,
    refreshToken: refreshToken,
    url,
    method: "GET",
  });

  let response = result.response;
  if (response?.status === 200) {
    return response.data;
  }
  return [];
};

export const isAppSpaceNameDuplicate = async (
  appSpaceName,
  accessToken,
  refreshToken
) => {
  let baseUrl = getEnvironmentVariables().REACT_APP_MARKETPLACE_API_URL;
  let url =
    config.marketPlace.engineeringOps.applications.isDuplicateAppName.replace(
      "{appSpaceName}",
      appSpaceName
    );

  let data = {};

  var result = await apiCall({
    data,
    baseUrl,
    accessToken: accessToken,
    refreshToken: refreshToken,
    url,
    method: "Get",
  });

  let response = result.response;
  if (response?.status === 200) {
    return response.data;
  }
  return {};
};

export const createAppSpace = async ({
  tenantId,
  organisationId,
  name,
  description,
  accessToken,
  refreshToken,
}) => {
  let baseUrl = getEnvironmentVariables().REACT_APP_MARKETPLACE_API_URL;
  // Resolves to POST v1/app-space on the Marketplace API. The backend grants the
  // caller the APPSPACE-OWNER role (OwnerUserId defaults to the signed-in user).
  let url = config.marketPlace.engineeringOps.applications.createNewApplication;

  let data = {
    name,
    description,
    tenantId,
    organisationId,
  };

  let result = await apiCall({
    data,
    baseUrl,
    accessToken: accessToken,
    refreshToken: refreshToken,
    url,
    method: "POST",
  });

  let response = result.response;
  if (response?.status === 200) {
    return response.data;
  }
  return {};
};

export const updateAppSpace = async ({
  tenantId,
  appSpaceId,
  name,
  description,
  accessToken,
  refreshToken,
}) => {
  let baseUrl = getEnvironmentVariables().REACT_APP_MARKETPLACE_API_URL;
  let url = config.marketPlace.engineeringOps.applications.updateAppSpaceUrl;

  let data = {
    id: appSpaceId,
    tenantId,
    name,
    description,
  };

  let result = await apiCall({
    data,
    baseUrl,
    accessToken: accessToken,
    refreshToken: refreshToken,
    url,
    method: "PUT",
  });

  let response = result.response;
  if (response?.status === 200) {
    return response.data;
  }
  return {};
};

export const deleteAppSpace = async ({
  tenantId,
  appSpaceId,
  accessToken,
  refreshToken,
}) => {
  let baseUrl = getEnvironmentVariables().REACT_APP_MARKETPLACE_API_URL;
  let url = config.marketPlace.engineeringOps.applications.deleteAppSpaceUrl
    .replace("{appSpaceId}", appSpaceId)
    .replace("{tenantId}", tenantId);

  let data = {};

  let result = await apiCall({
    data,
    baseUrl,
    accessToken: accessToken,
    refreshToken: refreshToken,
    url,
    method: "DELETE",
  });

  let response = result.response;

  // Delete returns an empty body on success; surface status + any error payload.
  return {
    statusCode: response?.status,
    ...(response?.data || {}),
  };
};

export const submitSupportTicket = async ({
  ticket,
  accessToken,
  refreshToken,
}) => {
  let baseUrl = getEnvironmentVariables().REACT_APP_MARKETPLACE_API_URL;
  let url = config.marketPlace.submitSupportTicket;

  let result = await apiCall({
    data: ticket,
    baseUrl,
    accessToken: accessToken,
    refreshToken: refreshToken,
    url,
    method: "POST",
  });
  let response = result.response;
  if (response?.status === 200) {
    return {
      ...response.data,
      success: true,
    };
  }
  return {
    failed: true,
    code: result?.code,
    userFriendlyMessage: result?.userFriendlyMessage,
  };
};

export const fetchEntityTags = async (entityId) => {
  let baseUrl = getEnvironmentVariables().REACT_APP_MARKETPLACE_API_URL;
  let url = config.marketPlace.fetchEntityTags.replace("{entityId}", entityId);

  let result = await apiCall({
    baseUrl,
    url,
    method: "Get",
  });

  let response = result.response;
  if (response?.status === 200) {
    return response.data;
  }
  return {};
};

export const fetchUserTeams = async ({
  userId,
  tenantId,
  organisationId,
  accessToken,
  refreshToken,
}) => {
  let baseUrl = getEnvironmentVariables().REACT_APP_MARKETPLACE_API_URL;

  let url = config.marketPlace.fetchUserTeamsUrl
    .replace("{userId}", userId)
    .replace("{tenantId}", tenantId)
    .replace("{organisationId}", organisationId);

  let result = await apiCall({
    baseUrl,
    url,
    method: "Get",
    accessToken,
    refreshToken,
  });

  let response = result.response;
  if (response?.status === 200) {
    return response.data;
  }

  return {};
};

export const submitFeedback = async (feedback, accessToken, refreshToken) => {
  let baseUrl = getEnvironmentVariables().REACT_APP_MARKETPLACE_API_URL;
  let url = config.marketPlace.postFeedbackUrl;

  let result = await apiCall({
    baseUrl,
    url,
    accessToken,
    refreshToken,
    data: feedback,
    method: "Post",
  });

  let response = result.response;
  if (response?.status === 200) {
    return response.data;
  }
  return {};
};

export const adjustResourceAccessForUser = async ({
  resourceId,
  organisationId,
  tenantId,
  data,
  accessToken,
  refreshToken,
}) => {
  let baseUrl = getEnvironmentVariables().REACT_APP_MARKETPLACE_API_URL;

  let url = config.marketPlace.access.adjustUserResourceAccessUrl
    .replace("{resourceId}", resourceId)
    .replace("{tenantId}", tenantId)
    .replace("{organisationId}", organisationId);

  let result = await apiCall({
    baseUrl,
    url,
    data,
    method: "Post",
    accessToken,
    refreshToken,
  });

   let response = result?.response;

   if (response && response?.status === General.StatusCodes.OK) {
     return response.data;
   }

   return {
     failed: true,
     message: result.userFriendlyMessage,
   };
};

// One call to a team address of Marketplace. Returns { success: true, data } or apiCall's
// normalised failure, so a screen checks success on the answer and words a failure from it.
const callTeamAddress = async ({ url, method, data, accessToken, refreshToken }) => {
  const result = await apiCall({
    baseUrl: getEnvironmentVariables().REACT_APP_MARKETPLACE_API_URL,
    url,
    data,
    method,
    accessToken,
    refreshToken,
  });

  if (result.success) {
    return { success: true, data: result.response.data };
  }

  return result;
};

// Gives a team a role on an app, an AppSpace, an organisation or the tenant, or takes it away,
// as the request says. Returns { success: true, data } or apiCall's normalised failure (403 for
// a role on an organisation or the tenant the user may not manage the users of).
export const adjustResourceAccessForTeam = async ({
  resourceId,
  organisationId,
  tenantId,
  data,
  accessToken,
  refreshToken,
}) => {
  const url = config.marketPlace.access.adjustTeamResourceAccessUrl
    .replace("{resourceId}", resourceId)
    .replace("{tenantId}", tenantId)
    .replace("{organisationId}", organisationId);

  return callTeamAddress({ url, method: "Post", data, accessToken, refreshToken });
};

// The teams of an organisation, or of every organisation of the tenant when the level is
// Tenant. A team belongs to the organisation it was made in and can be given a role anywhere
// in its tenant, so the list a team is picked from is the tenant's. An empty query lists them
// all. Returns { success: true, data: teams } or apiCall's normalised failure.
export const filterTeams = async ({
  tenantId,
  organisationId,
  query = "",
  level = Dashboard.Analytics.Levels.Organisation,
  accessToken,
  refreshToken,
}) => {
  const url = config.marketPlace.teams.filterOrganisationTeamsUrl
    .replace("{tenantId}", tenantId)
    .replace("{organisationId}", organisationId)
    .replace("{query}", encodeURIComponent(query))
    .replace("{level}", level);

  return callTeamAddress({ url, method: "Get", accessToken, refreshToken });
};

// Creates a team with no members in an organisation. Returns { success: true, data: team } or
// apiCall's normalised failure (400 for a name another team of the organisation has).
export const createTeam = async ({
  tenantId,
  organisationId,
  name,
  description,
  accessToken,
  refreshToken,
}) => {
  const url = config.marketPlace.teams.createTeamUrl
    .replace("{tenantId}", tenantId)
    .replace("{organisationId}", organisationId);

  return callTeamAddress({ url, method: "Post", data: { name, description }, accessToken, refreshToken });
};

// Changes a team's name and description. Returns { success: true, data: team } or apiCall's
// normalised failure.
export const updateTeam = async ({
  tenantId,
  organisationId,
  teamId,
  name,
  description,
  accessToken,
  refreshToken,
}) => {
  const url = config.marketPlace.teams.updateTeamUrl
    .replace("{tenantId}", tenantId)
    .replace("{organisationId}", organisationId)
    .replace("{teamId}", teamId);

  return callTeamAddress({ url, method: "Put", data: { name, description }, accessToken, refreshToken });
};

// Deletes a team with its members and every role it holds. Returns success or apiCall's
// normalised failure.
export const deleteTeam = async ({
  tenantId,
  organisationId,
  teamId,
  accessToken,
  refreshToken,
}) => {
  const url = config.marketPlace.teams.deleteTeamUrl
    .replace("{tenantId}", tenantId)
    .replace("{organisationId}", organisationId)
    .replace("{teamId}", teamId);

  return callTeamAddress({ url, method: "Delete", accessToken, refreshToken });
};

// Adds a person to a team. Returns { success: true, data: team }, the team with its members as
// they now stand, or apiCall's normalised failure.
export const addTeamMember = async ({
  tenantId,
  organisationId,
  teamId,
  userId,
  accessToken,
  refreshToken,
}) => {
  const url = config.marketPlace.teams.addTeamMemberUrl
    .replace("{tenantId}", tenantId)
    .replace("{organisationId}", organisationId)
    .replace("{teamId}", teamId)
    .replace("{userId}", userId);

  return callTeamAddress({ url, method: "Put", accessToken, refreshToken });
};

// Removes a person from a team. Returns { success: true, data: team }, the team with its
// members as they now stand, or apiCall's normalised failure.
export const removeTeamMember = async ({
  tenantId,
  organisationId,
  teamId,
  userId,
  accessToken,
  refreshToken,
}) => {
  const url = config.marketPlace.teams.removeTeamMemberUrl
    .replace("{tenantId}", tenantId)
    .replace("{organisationId}", organisationId)
    .replace("{teamId}", teamId)
    .replace("{userId}", userId);

  return callTeamAddress({ url, method: "Delete", accessToken, refreshToken });
};

export const toggleProductMcp = async ({
  tenantId,
  organisationId,
  productId,
  enabled,
  accessToken,
  refreshToken,
}) => {
  let baseUrl = getEnvironmentVariables().REACT_APP_MARKETPLACE_API_URL;
  let url = config.marketPlace.mcp.toggleProductMcpUrl
    .replace("{tenantId}", tenantId)
    .replace("{organisationId}", organisationId)
    .replace("{productId}", productId);

  var result = await apiCall({
    data: { enabled },
    baseUrl,
    accessToken,
    refreshToken,
    url,
    method: "PUT",
  });

  let response = result?.response;
  if (response?.status === 200) {
    return { success: true, data: response.data };
  }
  return { success: false, message: response?.data?.description };
};

export const fetchMcpTools = async ({
  tenantId,
  organisationId,
  productId,
  accessToken,
  refreshToken,
}) => {
  let baseUrl = getEnvironmentVariables().REACT_APP_MARKETPLACE_API_URL;
  let url = config.marketPlace.mcp.fetchToolsUrl
    .replace("{tenantId}", tenantId)
    .replace("{organisationId}", organisationId)
    .replace("{productId}", productId);

  var result = await apiCall({
    data: {},
    baseUrl,
    accessToken,
    refreshToken,
    url,
    method: "GET",
  });

  let response = result?.response;
  if (response?.status === 200) {
    return { success: true, data: response.data };
  }
  return { success: false, data: [] };
};

export const createMcpTool = async ({
  tenantId,
  organisationId,
  productId,
  tool,
  accessToken,
  refreshToken,
}) => {
  let baseUrl = getEnvironmentVariables().REACT_APP_MARKETPLACE_API_URL;
  let url = config.marketPlace.mcp.createToolUrl
    .replace("{tenantId}", tenantId)
    .replace("{organisationId}", organisationId)
    .replace("{productId}", productId);

  var result = await apiCall({
    data: tool,
    baseUrl,
    accessToken,
    refreshToken,
    url,
    method: "POST",
  });

  let response = result?.response;
  if (response?.status === 200) {
    return { success: true, data: response.data };
  }
  return { success: false, message: result?.userFriendlyMessage };
};

export const updateMcpTool = async ({
  tenantId,
  organisationId,
  productId,
  toolId,
  tool,
  accessToken,
  refreshToken,
}) => {
  let baseUrl = getEnvironmentVariables().REACT_APP_MARKETPLACE_API_URL;
  let url = config.marketPlace.mcp.updateToolUrl
    .replace("{tenantId}", tenantId)
    .replace("{organisationId}", organisationId)
    .replace("{productId}", productId)
    .replace("{toolId}", toolId);

  var result = await apiCall({
    data: tool,
    baseUrl,
    accessToken,
    refreshToken,
    url,
    method: "PUT",
  });

  let response = result?.response;
  if (response?.status === 200) {
    return { success: true, data: response.data };
  }
  return { success: false, message: result?.userFriendlyMessage };
};

export const deleteMcpTool = async ({
  tenantId,
  organisationId,
  productId,
  toolId,
  accessToken,
  refreshToken,
}) => {
  let baseUrl = getEnvironmentVariables().REACT_APP_MARKETPLACE_API_URL;
  let url = config.marketPlace.mcp.deleteToolUrl
    .replace("{tenantId}", tenantId)
    .replace("{organisationId}", organisationId)
    .replace("{productId}", productId)
    .replace("{toolId}", toolId);

  var result = await apiCall({
    data: {},
    baseUrl,
    accessToken,
    refreshToken,
    url,
    method: "DELETE",
  });

  let response = result?.response;
  if (response?.status === 200 || response?.status === 204) {
    return { success: true };
  }
  return { success: false, message: result?.userFriendlyMessage };
};

export const toggleMcpTool = async ({
  tenantId,
  organisationId,
  productId,
  toolId,
  enabled,
  accessToken,
  refreshToken,
}) => {
  let baseUrl = getEnvironmentVariables().REACT_APP_MARKETPLACE_API_URL;
  let url = config.marketPlace.mcp.toggleToolUrl
    .replace("{tenantId}", tenantId)
    .replace("{organisationId}", organisationId)
    .replace("{productId}", productId)
    .replace("{toolId}", toolId);

  var result = await apiCall({
    data: { enabled },
    baseUrl,
    accessToken,
    refreshToken,
    url,
    method: "PUT",
  });

  let response = result?.response;
  if (response?.status === 200) {
    return { success: true, data: response.data };
  }
  return { success: false, message: result?.userFriendlyMessage };
};


/**
 * Resolves an AppSpace slug within one organisation (deep-linking design).
 * A retired slug answers with the AppSpace that used to carry it and its
 * current slug as the redirect target. Returns null when nothing resolves.
 */
export const resolveAppSpaceSlug = async ({
  tenantId,
  organisationId,
  slug,
  accessToken,
  refreshToken,
}) => {
  let baseUrl = getEnvironmentVariables().REACT_APP_MARKETPLACE_API_URL;
  let url = config.marketPlace.engineeringOps.applications.resolveAppSpaceSlugUrl
    .replace("{tenantId}", tenantId)
    .replace("{organisationId}", organisationId)
    .replace("{slug}", encodeURIComponent(slug));

  let result = await apiCall({
    data: {},
    baseUrl,
    accessToken,
    refreshToken,
    url,
    method: "Get",
  });

  return result.response?.status === 200 ? result.response.data : null;
};
